import mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';

import MapBox from './MapBox.ts';
import { INITIAL_STYLE } from './constants.ts';
import { UseMapProps } from './types.ts';
import { usePinLayerCallbacks } from './usePinLayerCallbacks.ts';
import { usePinLayerCursor } from './usePinLayerCursor.ts';
import { usePolygonDraw } from './usePolygonDraw.ts';
import { useSubjectLayerCallback } from './useSubjectLayerCallback.ts';

function useMap<Feature = void>({
  mapContainerRef,
  mapRef,
  subject,
  center,
  isEnabled = true,
  enablePolygonDraw = false,
  enableStatusFiltering = false,
  selectedStatuses,
  callbacks = {},
  zoom,
  pins,
}: UseMapProps<Feature>) {
  useEffect(() => {
    const shouldRenderMap =
      isEnabled && mapContainerRef.current !== null && mapRef.current === null;
    if (!shouldRenderMap) return;

    const init = (instance: MapBox) => {
      const { getMap, getDraw } = instance;
      const map = getMap();
      const draw = getDraw();

      map.addControl(new mapboxgl.NavigationControl());
      map.addControl(new mapboxgl.FullscreenControl());
      if (enablePolygonDraw) map.addControl(draw as any);
    };

    mapRef.current = new MapBox(
      // @ts-expect-error `mapContainerRef.current` will not be `null` here
      mapContainerRef.current,
      [center.lng, center.lat],
      {
        styleURL: INITIAL_STYLE.url,
        zoom,
      },
      init,
    );
  }, [
    callbacks,
    center.lat,
    center.lng,
    enablePolygonDraw,
    mapContainerRef,
    mapRef,
    subject,
    zoom,
    isEnabled,
    pins,
  ]);

  useSubjectLayerCallback({
    mapRef,
    subject,
    center,
    callbacks: {
      onMapLoad: callbacks.onMapLoad,
      onStyleLoad: callbacks.onStyleLoad,
    },
  });

  usePinLayerCallbacks({
    mapRef,
    pins,
    selectedStatuses,
    enableStatusFiltering,
    callbacks: {
      onPinFilterChange: callbacks.onPinFilterChange,
      onMapLoad: callbacks.onMapLoad,
      onPinSourceLoad: callbacks.onPinSourceLoad,
      onPinClick: callbacks.onPinClick,
      onMoveEnd: callbacks.onMoveEnd,
    },
  });

  usePolygonDraw({
    mapRef,
    isEnabled: enablePolygonDraw,
    callbacks: {
      onPolygonDrawCreate: callbacks.onPolygonDrawCreate,
      onPolygonDrawUpdate: callbacks.onPolygonDrawUpdate,
      onPolygonDrawDelete: callbacks.onPolygonDrawDelete,
    },
  });

  usePinLayerCursor({
    mapRef,
    isEnabled: enablePolygonDraw,
  });
}

export { useMap };
